/**
 * Infinite Scroll for Collection Products
 * Automatically loads more products when user scrolls near the bottom of the page
 */

class InfiniteScroll {
  constructor() {
    this.isLoading = false;
    this.trigger = document.querySelector('.infinite-scroll-trigger');
    this.loadingIndicator = document.querySelector('.infinite-scroll-loading');
    this.productGrid = document.querySelector('#product-grid');
    
    if (!this.trigger || !this.productGrid) return;
    
    this.init();
  }

  init() {
    // Create Intersection Observer for automatic loading
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before trigger is visible
        threshold: 0
      }
    );

    this.observer.observe(this.trigger);
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.isLoading) {
        this.loadMore();
      }
    });
  }

  async loadMore() {
    if (this.isLoading) return;

    const nextUrl = this.trigger.getAttribute('data-next-url');
    if (!nextUrl) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error('Network response was not ok');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get new products
      const newProducts = doc.querySelectorAll('#product-grid > li');
      
      if (newProducts.length > 0) {
        // Append new products to grid
        newProducts.forEach((product) => {
          this.productGrid.appendChild(product);
        });

        // Update trigger with next page URL
        const newTrigger = doc.querySelector('.infinite-scroll-trigger');
        if (newTrigger && newTrigger.getAttribute('data-next-url')) {
          this.trigger.setAttribute('data-next-url', newTrigger.getAttribute('data-next-url'));
          this.hideLoading();
          this.isLoading = false;
        } else {
          // No more pages
          this.destroy();
        }

        // Announce to screen readers
        this.announceToScreenReader(`${newProducts.length} more products loaded`);

        // Reinitialize any components that need it (like quick add buttons)
        this.reinitializeComponents();
      } else {
        this.destroy();
      }
    } catch (error) {
      console.error('Error loading more products:', error);
      this.hideLoading();
      this.isLoading = false;
    }
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'block';
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
  }

  destroy() {
    // Remove observer and hide trigger
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.trigger) {
      this.trigger.remove();
    }
    this.hideLoading();
  }

  announceToScreenReader(message, priority = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.className = 'visually-hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }

  reinitializeComponents() {
    // Reinitialize quick-add buttons if they exist
    if (typeof window.QuickAddBulk !== 'undefined') {
      document.querySelectorAll('quick-add-bulk').forEach(element => {
        if (!element.initialized) {
          new window.QuickAddBulk(element);
        }
      });
    }

    // Trigger any animation effects for newly loaded items
    if (document.documentElement.classList.contains('animations-reveal-on-scroll')) {
      const newItems = this.productGrid.querySelectorAll('.scroll-trigger:not(.animate--slide-in-up)');
      newItems.forEach((item, index) => {
        setTimeout(() => {
          item.classList.add('animate--slide-in-up');
        }, index * 50);
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new InfiniteScroll();
  });
} else {
  new InfiniteScroll();
}

// Reinitialize after AJAX filter updates
document.addEventListener('facets:updated', () => {
  new InfiniteScroll();
});

